import dataProvider from "src/dataProvider";
import { fileUploadOcrType } from "src/types/api";
import { UploadProgress } from "src/types/upload";
import { createProgressTracker } from "src/utils/uploadProgress";
import { ResumableUploadApi, uploadFileResumable } from "./resumableUpload";

let fetcher = dataProvider("imageUploadInstance");
let adminFetcher = dataProvider("adminInstance");

/**
 * Files at or above this size are sent in resumable parts instead of one
 * request. Matches MULTIPART_THRESHOLD in document-upload-service.
 */
const MULTIPART_THRESHOLD = 8 * 1024 * 1024;

export type UploadOptions = {
  /** Receives bytes sent, transfer rate and time remaining as the file uploads. */
  onProgress?: (progress: UploadProgress) => void;
};

export const fileUploadOcr = async (data: fileUploadOcrType) => {
  return fetcher.post<fileUploadOcrType>("/upload/image", data);
};

// Browsers leave `type` empty for formats they don't recognise (.kml is the
// common one here); the service maps that back via the file extension.
const resolveContentType = (file: File) =>
  file.type || "application/octet-stream";

/** Small files: one request, exactly as before. */
const uploadInOneRequest = async (file: File, options: UploadOptions) => {
  const reportProgress = createProgressTracker(file.size, options.onProgress);

  const formData = new FormData();
  formData.append("file", file);

  return fetcher.post<FormData>(
    "/upload/file",
    formData,
    {},
    "v1",
    {},
    { onUploadProgress: (event: ProgressEvent) => reportProgress(event.loaded) }
  );
};

const resumableApi: ResumableUploadApi = {
  createSession: async (file, contentType) => {
    const { data } = await fetcher.post<
      { fileName: string; contentType: string; fileSize: number },
      { data: any }
    >("/upload/multipart/create", {
      fileName: file.name,
      contentType,
      fileSize: file.size,
    });

    return data.data;
  },

  uploadPart: async (session, partNumber, chunk, onChunkProgress) => {
    const formData = new FormData();
    formData.append("key", session.key);
    formData.append("uploadId", session.uploadId);
    formData.append("partNumber", String(partNumber));
    formData.append("file", chunk, `${session.key}.part${partNumber}`);

    await fetcher.post<FormData>(
      "/upload/multipart/part",
      formData,
      {},
      "v1",
      {},
      {
        onUploadProgress: (event: ProgressEvent) =>
          onChunkProgress(event.loaded),
      }
    );
  },

  getUploadedParts: async (session) => {
    const { data } = await fetcher.post<any, { data: any }>(
      "/upload/multipart/parts",
      { key: session.key, uploadId: session.uploadId }
    );

    return data.data.parts;
  },
};

/** Large files: chunked, retried per part and resumable after a drop. */
const uploadInParts = async (file: File, options: UploadOptions) => {
  const reportProgress = createProgressTracker(file.size, options.onProgress);

  const { key, uploadId } = await uploadFileResumable(
    file,
    resolveContentType(file),
    resumableApi,
    { onBytes: reportProgress }
  );

  return fetcher.post<{ key: string; uploadId: string }>(
    "/upload/multipart/complete",
    { key, uploadId }
  );
};

export const fileUpload = async (
  data: FormData,
  options: UploadOptions = {}
) => {
  const file = data.get("file") as File;

  if (!file) {
    throw new Error("No file provided for upload");
  }

  return file.size >= MULTIPART_THRESHOLD
    ? uploadInParts(file, options)
    : uploadInOneRequest(file, options);
};

export const getFileByKey = async (key: string) => {
  return adminFetcher.get(`/upload/file/${key}`);
};
